/**
 * Phase 27.37.F — Agent SMS notifications master toggle.
 *
 * Rendered on `NotificationPreferencesPage` above the existing
 * In-App/Email toggles. Enforces the affirmative consent flow
 * required by Twilio A2P for the Agent Messaging Service:
 *   - SMS is OFF by default.
 *   - Enabling SMS opens a dialog with the exact disclosure text.
 *   - Consent record is tied to the mobile currently on file.
 *   - Turning SMS OFF calls opt-out immediately.
 *   - When the phone changes, the backend surfaces
 *     `requires_new_opt_in` and we render the "re-consent" state.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageSquare, ShieldCheck, ShieldOff, AlertTriangle, Loader2, Send } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

export default function AgentSmsPreference() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/agent-notifications/sms/status');
      setStatus(r.data);
    } catch (e) {
      // Not every user is an agent — no-op silently for those.
      setStatus({ _unavailable: true });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-slate-400" size={20} />
      </div>
    );
  }
  if (!status || status._unavailable) return null;

  const requiresNewOptIn = !!status.requires_new_opt_in;
  const enabled = !!status.sms_enabled;

  // Normalise the phone input to E.164 for validation. Not stored
  // client-side — the backend normalises again on submission.
  const digits = (phoneInput || '').replace(/\D/g, '');
  const phoneValid = (
    (phoneInput.trim().startsWith('+') && digits.length >= 8)
    || digits.length === 10
    || (digits.length === 11 && digits.startsWith('1'))
  );

  const handleToggle = async (nextValue) => {
    if (nextValue) {
      // Open the atomic phone-+-consent form. Prepopulate with the
      // number currently on file (if any), never pre-check consent.
      setAccepted(false);
      setPhoneInput(status.current_phone_display || status.current_phone || '');
      setPhoneError('');
      setDialogOpen(true);
      return;
    }
    // Turning OFF: this is an opt-out.
    setSaving(true);
    try {
      await api.post('/agent-notifications/sms/opt-out', { reason: 'user_toggle_off' });
      toast.success('SMS notifications turned off');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to turn off SMS')); }
    finally { setSaving(false); }
  };

  const confirmOptIn = async () => {
    if (!accepted || !phoneValid) return;
    setSaving(true); setPhoneError('');
    try {
      await api.post('/agent-notifications/sms/opt-in', {
        accepted: true,
        phone: phoneInput.trim(),
        consent_language_version: status.consent_language_version,
      });
      toast.success('SMS notifications enabled');
      setDialogOpen(false);
      await load();
    } catch (e) {
      const msg = getErrorMessage(e, 'Failed to enable SMS');
      if (/mobile/i.test(msg)) setPhoneError(msg);
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/agent-notifications/sms/test');
      if (r.data?.ok) {
        toast.success(`Test SMS dispatched — status=${r.data.status || 'sent'}. Check your phone in ~30 seconds.`);
      } else {
        toast.error(r.data?.message || `Send blocked: ${r.data?.reason || 'unknown reason'}`);
      }
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Test SMS failed'));
    } finally { setTesting(false); }
  };

  return (
    <>
      <div
        className="p-4 rounded-lg border border-slate-200 space-y-3"
        data-testid="agent-sms-preference"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <MessageSquare size={16} className="text-emerald-600 mt-0.5" />
            <div>
              <Label className="text-sm font-medium">Text Message (SMS/MMS)</Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Voluntary. Off by default. Enable to receive operational SMS at
                the mobile number on your profile.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="text-slate-500">SMS will be sent to:</span>
                <span className="font-mono text-slate-800" data-testid="agent-sms-phone">
                  {status.current_phone_display || status.current_phone || <em>no phone on file</em>}
                </span>
                {status.consent_phone && enabled && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                    <ShieldCheck size={10} className="mr-1" /> Consent on file
                  </Badge>
                )}
                {status.opted_out && (
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                    <ShieldOff size={10} className="mr-1" /> Opted out
                  </Badge>
                )}
                {requiresNewOptIn && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                    <AlertTriangle size={10} className="mr-1" /> Phone changed — re-consent required
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
            data-testid="agent-sms-toggle"
          />
        </div>
        {/* Phase 27.37.I — send-time diagnostics + self-test button.
            Reveals why an opted-in agent still isn't receiving SMS
            (e.g. server-side channel_not_configured) and lets the
            agent verify delivery without waiting for a scheduled
            trigger. */}
        {!status.send_allowed && status.block_reason && (
          <div
            className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2"
            data-testid="agent-sms-block-reason"
          >
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">SMS delivery is blocked</div>
              <div className="text-amber-700 mt-0.5">
                Reason: <code className="font-mono">{status.block_reason}</code>
              </div>
              {status.block_reason === 'channel_not_configured' && (
                <div className="mt-1">
                  The server does not have <code>TWILIO_AGENT_MESSAGING_SERVICE_SID</code> or
                  <code>TWILIO_PHONE_NUMBER_AGENT</code> configured. Contact KR support to
                  enable the Agent SMS channel in production.
                </div>
              )}
              {status.block_reason === 'phone_changed' && (
                <div className="mt-1">
                  Toggle SMS off and back on above to re-consent with your current number.
                </div>
              )}
            </div>
          </div>
        )}
        {enabled && (
          <div className="pt-2 border-t border-slate-100">
            <Button
              size="sm"
              variant="outline"
              onClick={sendTest}
              disabled={testing || !status.send_allowed}
              data-testid="agent-sms-send-test"
            >
              {testing
                ? <Loader2 className="animate-spin mr-2" size={12} />
                : <Send size={12} className="mr-2" />}
              Send test SMS to myself
            </Button>
            {!status.send_allowed && (
              <span className="ml-2 text-[11px] text-amber-600">
                Fix the delivery block above first.
              </span>
            )}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="agent-sms-consent-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enable SMS/MMS Notifications</DialogTitle>
            <DialogDescription>
              Enter the mobile number that should receive SMS, then affirmatively accept the disclosure.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Mobile phone field — pre-populated, editable, required. */}
            <div className="space-y-1.5">
              <Label htmlFor="agent-sms-phone-input" className="text-sm font-medium">
                Mobile Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="agent-sms-phone-input"
                type="tel"
                value={phoneInput}
                onChange={(e) => { setPhoneInput(e.target.value); setPhoneError(''); }}
                placeholder="+1 (813) 555-1212"
                autoComplete="tel"
                data-testid="agent-sms-phone-input"
              />
              <p className="text-[11px] text-slate-500">
                US-format numbers (10 digits) or full international format (starting with +) are accepted.
                We normalize to E.164 (e.g. +18135551212) and store this as your consent-tied number.
              </p>
              {phoneError && (
                <p className="text-xs text-red-600" data-testid="agent-sms-phone-error">{phoneError}</p>
              )}
            </div>

            {/* Disclosure block — checkbox stays UNCHECKED by default. */}
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 leading-relaxed max-h-[40vh] overflow-y-auto space-y-2">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="agent-sms-accept"
                  checked={accepted}
                  onCheckedChange={setAccepted}
                  className="mt-0.5"
                  data-testid="agent-sms-accept-checkbox"
                />
                <label htmlFor="agent-sms-accept" className="cursor-pointer">
                  I agree to receive SMS/MMS messages from KR Enterprise Group at the mobile number provided above.
                </label>
              </div>
              <p className="pl-6">
                Messages may include account notifications, contracting updates, follow-up reminders,
                training/event reminders, and other operational communications. Message frequency varies.
                Message and data rates may apply. Consent is not a condition of employment, contracting,
                or participation. Reply STOP to opt out or HELP for help.
              </p>
              <p className="pl-6 text-slate-500">
                See our <a href="https://krenterprisegroup.app/privacy" target="_blank" rel="noreferrer" className="text-blue-600 underline">Privacy Policy</a>{' '}
                and <a href="https://krenterprisegroup.app/terms" target="_blank" rel="noreferrer" className="text-blue-600 underline">Terms &amp; Conditions</a>.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={confirmOptIn}
              disabled={!accepted || !phoneValid || saving}
              data-testid="agent-sms-confirm-opt-in"
            >
              {saving && <Loader2 className="animate-spin mr-1" size={14} />}
              Enable SMS Notifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
